import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  InfoBox,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  kstDateStr,
} from '@biznomi/ui-react'
import { createEmployee, type Department, type EmployeeRow } from '@/lib/api/admin'
import { useInitialPasswordRule, initialPasswordHintOne } from '@/lib/initial-password'

/**
 * 직원 등록 — ADMIN 전용.
 *
 * ★ 초기 비밀번호를 화면에서 정하지 않는다.
 *   서버가 조직 규칙대로 정한다(사번 있으면 사번, 없으면 조직 폴백). 화면에서 받으면
 *   조직마다 규칙이 갈리고, 그 값이 브라우저와 네트워크를 한 번 더 거친다.
 *   등록 후 본인이 첫 로그인에서 바꾼다 (mustChangePassword).
 *
 * ★ 필수 항목은 조직의 loginType 이 정한다.
 *   사번 로그인 조직에서 사번 없이 등록하면 그 사람은 **로그인할 수단이 없다**.
 *   서버도 막지만, 등록 버튼을 누른 뒤에 알게 하면 이미 늦다.
 */

const NONE = '__none__'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  departments: Department[]
  /** 'EMPLOYEE_ID' 면 사번이, 아니면 이메일이 필수다 */
  loginType?: string
  onCreated: (employee: EmployeeRow) => void
}

export function EmployeeCreateDialog({ open, onOpenChange, departments, loginType, onCreated }: Props) {
  const qc = useQueryClient()

  /*
   * 조직이 첫 비밀번호를 무엇으로 정하는지 — 규칙과 문구는 `lib/initial-password.ts`
   * 한 곳에 있다. 일괄 등록도 같은 것을 쓴다.
   *
   * 🔴 전에는 여기서만 읽고 일괄 등록은 하드코딩이었다. 9/12 리허설에서 그게 터졌다.
   *   사본을 고치지 않고 합친 이유는 그 파일 주석에 적었다.
   */
  const pwRule = useInitialPasswordRule()
  const byEmployeeNumber = loginType === 'EMPLOYEE_ID'

  const [name, setName] = useState('')
  const [employeeNumber, setEmployeeNumber] = useState('')
  const [email, setEmail] = useState('')
  const [hireDate, setHireDate] = useState(() => kstDateStr())
  const [dept, setDept] = useState(NONE)
  const [role, setRole] = useState<EmployeeRow['role']>('EMPLOYEE')
  const [error, setError] = useState('')

  function reset() {
    setName('')
    setEmployeeNumber('')
    setEmail('')
    setHireDate(kstDateStr())
    setDept(NONE)
    setRole('EMPLOYEE')
    setError('')
  }

  // 서버 규칙과 같은 것만 본다 (employee.dto.ts CreateEmployeeSchema)
  const badEmail = email.length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  const missingLoginId = byEmployeeNumber ? !employeeNumber.trim() : !email.trim()
  const canSubmit = Boolean(name.trim()) && Boolean(hireDate) && !missingLoginId && !badEmail

  const create = useMutation({
    mutationFn: () =>
      createEmployee({
        name: name.trim(),
        hireDate,
        employeeNumber: employeeNumber.trim() || undefined,
        email: email.trim() || undefined,
        role,
        departmentId: dept === NONE ? null : dept,
      }),
    onSuccess: (res) => {
      if (!res.success || !res.data) {
        setError(res.error ?? '추가하지 못했습니다')
        return
      }
      qc.invalidateQueries({ queryKey: ['admin', 'employees'] })
      onCreated(res.data)
      reset()
      onOpenChange(false)
    },
  })

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset()
        onOpenChange(o)
      }}
    >
      <DialogContent className="max-h-[90dvh] max-w-md overflow-y-auto">
        <DialogTitle>직원 추가</DialogTitle>
        <DialogDescription>
          {byEmployeeNumber
            ? '이 회사는 사번으로 로그인합니다. 사번이 없으면 이 사람은 로그인을 못 합니다.'
            : '이 회사는 이메일로 로그인합니다.'}
        </DialogDescription>

        <div className="mt-4 space-y-3">
          <div>
            <label htmlFor="emp-name" className="mb-1 block text-sm text-slate-500">
              이름
            </label>
            <Input
              id="emp-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={50}
              className="h-10"
            />
          </div>

          <div>
            <label htmlFor="emp-number" className="mb-1 block text-sm text-slate-500">
              사번{byEmployeeNumber && <span className="text-[var(--color-danger)]"> *</span>}
            </label>
            <Input
              id="emp-number"
              value={employeeNumber}
              onChange={(e) => setEmployeeNumber(e.target.value)}
              maxLength={20}
              aria-invalid={byEmployeeNumber && !employeeNumber.trim() ? true : undefined}
              className="h-10"
            />
            {/*
              첫 비밀번호가 무엇인지 관리자가 알아야 직원에게 안내할 수 있다.
              ⚠ **조직 설정을 따른다.** 하드코딩하면 틀린 값을 알려 주게 된다 (위 주석).
              ⚠ 설정을 아직 못 읽었으면 아무 말도 안 한다 — 틀린 안내보다 낫다.

              🔴 폴백일 때 **다음 행동까지 적는다.** 조직 공통 비밀번호는 조직을 만들 때
                 화면에 한 번 뜨고 끝이라, 관리자가 그 값을 모르는 경우가 흔하다
                 (지금은 그걸 보거나 바꾸는 화면이 없다 — 슈퍼어드민만 할 수 있다).
                 그런데 **막다른 길이 아니다.** 직원 목록의 «임시 비밀번호» 버튼으로
                 그 사람 것만 따로 발급하면 된다. 그 길을 여기서 가리킨다.
            */}
            {pwRule && (
              <p className="mt-1 text-xs text-slate-400">
                {initialPasswordHintOne(pwRule, employeeNumber)}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="emp-email" className="mb-1 block text-sm text-slate-500">
              이메일{!byEmployeeNumber && <span className="text-[var(--color-danger)]"> *</span>}
            </label>
            <Input
              id="emp-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-invalid={badEmail ? true : undefined}
              className="h-10"
            />
            {badEmail && (
              <p className="mt-1 text-xs text-[var(--color-danger)]">이메일 형식이 아닙니다</p>
            )}
          </div>

          <div>
            <label htmlFor="emp-hire" className="mb-1 block text-sm text-slate-500">
              입사일
            </label>
            <Input
              id="emp-hire"
              type="date"
              value={hireDate}
              onChange={(e) => setHireDate(e.target.value)}
              className="h-10"
            />
          </div>

          {/* 부서가 없는 조직(소상공인)에서는 이 칸 자체를 감춘다 — D16 */}
          {departments.length > 0 && (
            <div>
              <label className="mb-1 block text-sm text-slate-500">부서</label>
              <Select value={dept} onValueChange={setDept}>
                <SelectTrigger className="h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>미지정</SelectItem>
                  {departments.map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <label className="mb-1 block text-sm text-slate-500">역할</label>
            <Select value={role} onValueChange={(v) => setRole(v as EmployeeRow['role'])}>
              <SelectTrigger className="h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="EMPLOYEE">직원</SelectItem>
                <SelectItem value="MANAGER">관리자</SelectItem>
                <SelectItem value="ADMIN">최종관리자</SelectItem>
              </SelectContent>
            </Select>
            {role === 'ADMIN' && (
              <p className="mt-1 text-xs text-slate-400">
                최종관리자는 직원을 추가·퇴사시키고, 임시 비밀번호를 만들고, 보안 화면까지 볼 수
                있습니다. 회사 전체를 다룰 수 있는 권한입니다.
              </p>
            )}
          </div>

          {error && <InfoBox tone="danger">{error}</InfoBox>}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            취소
          </Button>
          <Button onClick={() => create.mutate()} loading={create.isPending} disabled={!canSubmit}>
            추가
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
